/**
 * / — oddits landing page (fully self-contained).
 * @file src/web/features/main/pages/index.tsx
 *
 * This page renders BARE (see App.tsx layouts — `/` is deliberately not
 * in the marketing layout group), so it ships its own header + footer
 * and owns the full viewport. A bespoke, modern landing experience for
 * oddits — practice management for chartered accountants.
 *
 * Design: the Olito x Oddits kit — Paper White canvas, Ink Black type and
 * actions, Highlighter Yellow used the way the kit describes it, as a pen
 * over a page: it marks the hero emphasis, the section eyebrows and the
 * figures that matter, and nothing else.
 *
 * No gradients. The kit is four flat colours and says tints are "for
 * backgrounds and secondary states, never for introducing a new colour" —
 * an indigo→violet ramp is a fifth colour by another name.
 *
 * Yellow is a SURFACE, never a button or text colour: Paper White on
 * Highlighter is 1.24:1. Every action is Ink Black, which is 14.07:1 on
 * yellow and 17.4:1 on white.
 *
 * Self-contained on purpose: no dependency on the shared Header/Footer.
 */

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { SEO } from '../../../shared/components';
import { brand } from '../../../shared/brand';
import {
  FileSpreadsheet,
  Users,
  CalendarClock,
  Scale,
  ShieldCheck,
  Receipt,
  ArrowRight,
  CheckCircle2,
  Upload,
  Sparkles,
  TrendingUp,
  Menu,
  X,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';

/* ── data ──────────────────────────────────────────────────────── */

const NAV = [
  { label: 'Features', href: '#features' },
  { label: 'Tally import', href: '#tally' },
  { label: 'How it works', href: '#how' },
  { label: 'FAQ', href: '#faq' },
];

const FEATURES: { icon: LucideIcon; title: string; body: string; accent: string }[] = [
  { icon: Users, title: 'All your clients, one place', body: 'Every client, entity, GSTIN, PAN and contact — organised and searchable. No more scattered sheets and WhatsApp notes.', accent: 'text-inkblack bg-highlighter-40' },
  { icon: FileSpreadsheet, title: 'Import straight from Tally', body: 'Pull ledgers and vouchers from a client’s TallyPrime in one step. Amounts, opening balances and double-entry stay intact.', accent: 'text-inkblack bg-highlighter-40' },
  { icon: Scale, title: 'Books that reconcile', body: 'Every import is checked — debits equal credits, ledgers resolve, balances tie out. You see a green tick, not a guess.', accent: 'text-inkblack bg-highlighter-40' },
  { icon: CalendarClock, title: 'Deadlines you won’t miss', body: 'GST, TDS, ROC and income-tax dates tracked per client with reminders. Know what’s due this week at a glance.', accent: 'text-inkblack bg-highlighter-40' },
  { icon: Receipt, title: 'Compliance at a glance', body: 'Filing status, pending returns and open tasks per client — so nothing slips between engagements.', accent: 'text-inkblack bg-highlighter-40' },
  { icon: ShieldCheck, title: 'Secure & audited', body: 'Role-based access for your team, a full audit trail of every action, and client financials that stay in your control.', accent: 'text-inkblack bg-highlighter-40' },
];

const STEPS: { icon: LucideIcon; title: string; body: string }[] = [
  { icon: Users, title: 'Add your clients', body: 'Set up each client in minutes — details, entities and team access.' },
  { icon: Upload, title: 'Bring in their books', body: 'Export from Tally and upload, or one-click sync. oddits parses, validates and files it.' },
  { icon: CheckCircle2, title: 'Stay on top', body: 'Track deadlines, review reconciled books, assign work — from one clean dashboard.' },
];

const RECONCILE_POINTS = [
  'Debits = credits on every voucher',
  'Every ledger resolves to your chart of accounts',
  'Opening balances and GST captured, not lost',
  'Re-import safely — updates, never duplicates',
];

const STATS = [
  ['1-click', 'Tally import'],
  ['100%', 'Reconciled before you look'],
  ['0', 'Rows re-keyed by hand'],
  ['1', 'Home for the whole practice'],
];

const FAQ = [
  { q: 'Do I need to change how my clients use Tally?', a: 'No. Your clients keep working in TallyPrime as usual. You export (or one-click sync) and oddits imports it — ledgers, vouchers and balances — validated automatically.' },
  { q: 'How do you know the numbers are right?', a: 'Every import is checked before it lands: debits must equal credits on each voucher, every ledger must resolve, and balances must tie out. Anything off is flagged, not silently loaded.' },
  { q: 'Is it built for Indian compliance?', a: 'Yes — GST, TDS, ROC and income-tax deadlines are first-class, tracked per client with reminders. GSTINs and PANs live on each client record.' },
  { q: 'Can my whole team use it?', a: 'Role-based access lets you invite staff and articles with the right permissions, and every action is captured in an audit trail.' },
];

/* ── page ──────────────────────────────────────────────────────── */

export default function HomePage() {
  return (
    <div className="min-h-screen bg-paper text-inkblack antialiased">
      <SEO />
      <SiteHeader />

      <main>
        <Hero />
        <TrustStrip />
        <Features />
        <TallyShowcase />
        <Stats />
        <HowItWorks />
        <Faq />
        <ClosingCta />
      </main>

      <SiteFooter />
    </div>
  );
}

/* ── header (hardcoded, sticky, solidifies on scroll) ──────────── */

function SiteHeader() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <header
      className={`sticky top-0 z-50 transition-all ${
        scrolled ? 'border-b border-receipt bg-paper/80 backdrop-blur-md' : 'border-b border-transparent bg-transparent'
      }`}
    >
      <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-3.5">
        <Link to="/" className="no-underline">
          <img src={brand.logo} alt="oddits" className="h-11 w-auto" />
        </Link>

        <nav className="hidden items-center gap-8 md:flex">
          {NAV.map((n) => (
            <a key={n.href} href={n.href} className="text-sm font-medium text-inkblack-60 no-underline transition-colors hover:text-inkblack">
              {n.label}
            </a>
          ))}
        </nav>

        <div className="hidden items-center gap-2 md:flex">
          <Link to="/auth/login" className="rounded-full px-4 py-2 text-sm font-semibold text-inkblack-80 no-underline transition-colors hover:text-inkblack">
            Sign in
          </Link>
          <Link
            to="/auth/register"
            className="group inline-flex items-center gap-1.5 rounded-full bg-inkblack px-4 py-2 text-sm font-semibold text-paper no-underline shadow-sm transition-all hover:-translate-y-0.5 hover:bg-inkblack-80 hover:shadow-md"
          >
            Start free
            <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5" />
          </Link>
        </div>

        <button className="md:hidden" onClick={() => setOpen((o) => !o)} aria-label="Menu">
          {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </div>

      {/* mobile menu */}
      {open && (
        <div className="border-t border-receipt bg-paper px-6 py-4 md:hidden">
          <nav className="flex flex-col gap-1">
            {NAV.map((n) => (
              <a key={n.href} href={n.href} onClick={() => setOpen(false)} className="rounded-lg px-2 py-2 text-sm font-medium text-inkblack-80 no-underline hover:bg-receipt-40">
                {n.label}
              </a>
            ))}
            <div className="mt-2 flex flex-col gap-2">
              <Link to="/auth/login" className="rounded-full border border-receipt px-4 py-2 text-center text-sm font-semibold text-inkblack-80 no-underline">Sign in</Link>
              <Link to="/auth/register" className="rounded-full bg-inkblack px-4 py-2 text-center text-sm font-semibold text-paper no-underline">Start free</Link>
            </div>
          </nav>
        </div>
      )}
    </header>
  );
}

/* ── hero ──────────────────────────────────────────────────────── */

function Hero() {
  return (
    <section className="relative isolate overflow-hidden">
      <div aria-hidden className="pointer-events-none absolute inset-x-0 -top-40 -z-10 flex justify-center">
        <div className="h-[36rem] w-[70rem] max-w-full bg-[radial-gradient(closest-side,rgba(255,231,125,0.55),rgba(255,231,125,0.18),transparent)] blur-2xl" />
      </div>
      <div aria-hidden className="pointer-events-none absolute inset-0 -z-10 opacity-40 [background-image:radial-gradient(circle,rgba(26,26,26,0.06)_1px,transparent_1px)] [background-size:22px_22px]" />

      <div className="mx-auto max-w-7xl px-6 pt-16 pb-24 text-center lg:pt-24">
        <span className="inline-flex items-center gap-1.5 rounded-full border border-highlighter bg-highlighter-40 px-3.5 py-1.5 text-xs font-medium text-inkblack">
          <Sparkles className="h-3.5 w-3.5" />
          Practice management, minus the busywork
        </span>

        <h1 className="mx-auto mt-6 max-w-4xl text-4xl font-bold tracking-tight sm:text-6xl">
          Run your practice,{' '}
          {/* The kit's own image for this colour: a pen over a page. The mark
              sits BEHIND the text and slightly overhangs it, the way a real
              highlighter never quite lines up with the words. */}
          <span className="relative inline-block whitespace-nowrap">
            <span
              aria-hidden
              className="absolute inset-x-[-0.12em] bottom-[0.06em] -z-10 h-[0.62em] -rotate-[0.6deg] rounded-[0.15em] bg-highlighter"
            />
            <span className="relative text-inkblack">not your spreadsheets</span>
          </span>.
        </h1>

        <p className="mx-auto mt-6 max-w-2xl text-lg text-inkblack-60 sm:text-xl">
          oddits brings every client, their Tally books and your compliance calendar into
          one place — imported and reconciled automatically. So your team spends its time on
          advice, not admin.
        </p>

        <div className="mt-9 flex flex-col items-center justify-center gap-3 sm:flex-row">
          <Link to="/auth/register" className="group inline-flex items-center gap-2 rounded-full bg-inkblack px-6 py-3 text-sm font-semibold text-paper no-underline shadow-lg transition-all hover:-translate-y-0.5 hover:bg-inkblack-80 hover:shadow-xl">
            Start free <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
          </Link>
          <a href="#tally" className="inline-flex items-center gap-2 rounded-full border border-receipt bg-paper/60 px-6 py-3 text-sm font-semibold text-inkblack-80 no-underline backdrop-blur transition-colors hover:border-inkblack-40 hover:bg-paper">
            See how it works
          </a>
        </div>
        <p className="mt-4 text-xs text-inkblack-60">Free to start · No card required · Built for Indian CA practices</p>

        <div className="mx-auto mt-16 max-w-3xl">
          <ImportPreviewCard />
        </div>
      </div>
    </section>
  );
}

function TrustStrip() {
  return (
    <section className="border-y border-receipt-80 bg-receipt-40/60">
      <div className="mx-auto flex max-w-7xl flex-wrap items-center justify-center gap-x-8 gap-y-3 px-6 py-6 text-sm text-inkblack-60">
        {['Tally import', 'GST & TDS deadlines', 'Reconciled books', 'Team roles', 'Full audit trail'].map((item) => (
          <span key={item} className="inline-flex items-center gap-1.5">
            <CheckCircle2 className="h-4 w-4 text-ok-500" />
            {item}
          </span>
        ))}
      </div>
    </section>
  );
}

function Features() {
  return (
    <section id="features" className="scroll-mt-20">
      <div className="mx-auto max-w-7xl px-6 py-20 sm:py-24">
        <div className="mx-auto max-w-2xl text-center">
          <h2 className="text-3xl font-semibold tracking-tight sm:text-4xl">Everything a practice needs. Nothing it doesn&rsquo;t.</h2>
          <p className="mt-4 text-inkblack-60">Purpose-built for chartered accountants — not a generic CRM bent into shape. Clear, fast, and quiet where it should be.</p>
        </div>
        <div className="mt-14 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {FEATURES.map((f) => (
            <div key={f.title} className="group rounded-2xl border border-receipt bg-paper p-6 transition-all hover:-translate-y-1 hover:border-receipt hover:shadow-lg hover:shadow-ink-200/60">
              <div className={`flex size-11 items-center justify-center rounded-xl ${f.accent}`}>
                <f.icon className="h-5 w-5" />
              </div>
              <h3 className="mt-5 text-lg font-semibold">{f.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-inkblack-60">{f.body}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function TallyShowcase() {
  return (
    <section id="tally" className="scroll-mt-20 border-y border-receipt-80 bg-receipt-40/60">
      <div className="mx-auto grid max-w-7xl items-center gap-12 px-6 py-20 lg:grid-cols-2 sm:py-24">
        <div>
          <span className="inline-flex items-center gap-1.5 rounded-full bg-highlighter-40 px-3 py-1 text-xs font-medium text-inkblack">
            <FileSpreadsheet className="h-3.5 w-3.5" /> Tally, done right
          </span>
          <h2 className="mt-4 text-3xl font-semibold tracking-tight sm:text-4xl">Your client&rsquo;s books, imported and reconciled.</h2>
          <p className="mt-4 text-inkblack-60">Re-keying Tally data is where hours vanish and errors creep in. oddits imports the full ledger and every voucher, then validates it before you ever look — so the numbers you work from are the numbers that tie out.</p>
          <ul className="mt-7 space-y-3.5">
            {RECONCILE_POINTS.map((p) => (
              <li key={p} className="flex items-start gap-3 text-sm text-inkblack-80">
                <span className="mt-0.5 flex size-5 shrink-0 items-center justify-center rounded-full bg-ok-100">
                  <CheckCircle2 className="h-3.5 w-3.5 text-ok-600" />
                </span>
                <span>{p}</span>
              </li>
            ))}
          </ul>
        </div>
        <ImportPreviewCard />
      </div>
    </section>
  );
}

function Stats() {
  return (
    <section className="relative isolate overflow-hidden bg-inkblack">
      <div aria-hidden className="pointer-events-none absolute inset-0 -z-10 bg-[radial-gradient(60rem_30rem_at_50%_-20%,rgba(255,231,125,0.22),transparent)]" />
      <div className="mx-auto grid max-w-7xl grid-cols-2 gap-8 px-6 py-16 lg:grid-cols-4">
        {STATS.map(([big, label]) => (
          <div key={label} className="text-center">
            <div className="text-3xl font-bold text-highlighter sm:text-4xl">{big}</div>
            <div className="mt-2 text-sm text-receipt">{label}</div>
          </div>
        ))}
      </div>
    </section>
  );
}

function HowItWorks() {
  return (
    <section id="how" className="scroll-mt-20">
      <div className="mx-auto max-w-7xl px-6 py-20 sm:py-24">
        <div className="mx-auto max-w-2xl text-center">
          <h2 className="text-3xl font-semibold tracking-tight sm:text-4xl">Up and running in an afternoon</h2>
          <p className="mt-4 text-inkblack-60">No migration project, no week of training. Add a client, bring in their books, get to work.</p>
        </div>
        <div className="relative mx-auto mt-14 grid max-w-5xl gap-8 md:grid-cols-3">
          <div aria-hidden className="absolute left-0 right-0 top-5 hidden h-px bg-gradient-to-r from-transparent via-receipt to-transparent md:block" />
          {STEPS.map((s, i) => (
            <div key={s.title} className="relative text-center">
              <div className="mx-auto flex size-11 items-center justify-center rounded-full border border-inkblack bg-highlighter text-inkblack shadow-sm">
                <s.icon className="h-5 w-5" />
              </div>
              <p className="mt-4 text-xs font-semibold uppercase tracking-wider text-inkblack-60">Step {i + 1}</p>
              <h3 className="mt-1 text-lg font-semibold">{s.title}</h3>
              <p className="mt-2 text-sm text-inkblack-60">{s.body}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function Faq() {
  return (
    <section id="faq" className="scroll-mt-20 border-t border-receipt-80 bg-receipt-40/60">
      <div className="mx-auto max-w-3xl px-6 py-20 sm:py-24">
        <h2 className="text-center text-3xl font-semibold tracking-tight sm:text-4xl">Questions, answered</h2>
        <div className="mt-10 divide-y divide-ink-200 overflow-hidden rounded-2xl border border-receipt bg-paper">
          {FAQ.map((f) => (
            <details key={f.q} className="group px-6 [&_summary]:list-none">
              <summary className="flex cursor-pointer items-center justify-between py-5 text-left text-base font-semibold text-inkblack">
                {f.q}
                <span className="ml-4 grid size-6 shrink-0 place-items-center rounded-full bg-receipt text-inkblack-60 transition-transform group-open:rotate-45">+</span>
              </summary>
              <p className="pb-5 text-sm leading-relaxed text-inkblack-60">{f.a}</p>
            </details>
          ))}
        </div>
      </div>
    </section>
  );
}

function ClosingCta() {
  return (
    <section className="mx-auto max-w-7xl px-6 py-20 sm:py-24">
      <div className="relative isolate overflow-hidden rounded-3xl bg-inkblack px-6 py-16 text-center shadow-xl sm:py-20">
        <div aria-hidden className="pointer-events-none absolute inset-0 -z-10 opacity-20 [background-image:radial-gradient(circle,#FFE77D_1px,transparent_1px)] [background-size:26px_26px]" />
        <h2 className="mx-auto max-w-2xl text-3xl font-semibold tracking-tight text-paper sm:text-4xl">Spend less time on admin, more on advice.</h2>
        <p className="mx-auto mt-4 max-w-lg text-receipt">Bring your practice into one simple, modern workspace. Start free — you could have your first client&rsquo;s books in tonight.</p>
        <div className="mt-8 flex flex-col items-center justify-center gap-3 sm:flex-row">
          <Link to="/auth/register" className="group inline-flex items-center gap-2 rounded-full bg-highlighter px-6 py-3 text-sm font-semibold text-inkblack no-underline shadow-lg transition-all hover:-translate-y-0.5 hover:bg-highlighter-80 hover:shadow-xl">
            Create free account <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
          </Link>
          <Link to="/contact" className="inline-flex items-center rounded-full border border-receipt/40 px-6 py-3 text-sm font-semibold text-paper no-underline transition-colors hover:bg-paper/10">Talk to us</Link>
        </div>
      </div>
    </section>
  );
}

/* ── footer (hardcoded) ────────────────────────────────────────── */

function SiteFooter() {
  const cols = [
    { title: 'Product', links: [['Features', '#features'], ['Tally import', '#tally'], ['How it works', '#how'], ['FAQ', '#faq']] },
    { title: 'Company', links: [['About', '/about'], ['Contact', '/contact']] },
    { title: 'Legal', links: [['Privacy', '/privacy'], ['Terms', '/terms'], ['Refund', '/refund'], ['Cancellation', '/cancellation']] },
  ];
  return (
    <footer className="border-t border-receipt bg-paper">
      <div className="mx-auto max-w-7xl px-6 py-14">
        <div className="grid gap-10 md:grid-cols-[1.5fr_1fr_1fr_1fr]">
          <div>
            <img src={brand.logo} alt="oddits" className="h-8 w-auto" />
            <p className="mt-4 max-w-xs text-sm text-inkblack-60">Modern practice management for chartered accountants. Clients, books, compliance and deadlines — one calm place.</p>
          </div>
          {cols.map((c) => (
            <div key={c.title}>
              <h4 className="text-xs font-semibold uppercase tracking-wider text-inkblack-60">{c.title}</h4>
              <ul className="mt-4 space-y-2.5">
                {c.links.map(([label, href]) => (
                  <li key={label}>
                    {href.startsWith('#') ? (
                      <a href={href} className="text-sm text-inkblack-60 no-underline transition-colors hover:text-inkblack">{label}</a>
                    ) : (
                      <Link to={href} className="text-sm text-inkblack-60 no-underline transition-colors hover:text-inkblack">{label}</Link>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
        <div className="mt-12 flex flex-col items-center justify-between gap-3 border-t border-receipt-80 pt-6 text-xs text-inkblack-60 sm:flex-row">
          <p>© {new Date().getFullYear()} oddits. All rights reserved.</p>
          <p>Made for CA practices in India.</p>
        </div>
      </div>
    </footer>
  );
}

/* ── product preview card ──────────────────────────────────────── */

function ImportPreviewCard() {
  return (
    <div className="rounded-2xl border border-receipt bg-paper p-5 text-left shadow-xl shadow-ink-300/40 sm:p-6">
      <div className="mb-4 flex items-center gap-1.5">
        {/* Window furniture, not status. The macOS traffic lights were three
            colours the kit does not have, doing no work. */}
        <span className="size-2.5 rounded-full bg-receipt" />
        <span className="size-2.5 rounded-full bg-receipt" />
        <span className="size-2.5 rounded-full bg-receipt" />
        <span className="ml-3 text-xs text-inkblack-60">oddits · Tally import</span>
      </div>
      <div className="flex items-center justify-between border-b border-receipt-80 pb-4">
        <div>
          <p className="text-sm font-semibold text-inkblack">Nimbus Traders Pvt Ltd</p>
          <p className="text-xs text-inkblack-60">Day Book · FY 2024&ndash;25</p>
        </div>
        <span className="inline-flex items-center gap-1 rounded-full bg-ok-50 px-2.5 py-1 text-xs font-medium text-ok-600">
          <CheckCircle2 className="h-3.5 w-3.5" /> Validated
        </span>
      </div>
      <dl className="mt-4 grid grid-cols-2 gap-3 text-sm">
        {[['Vouchers', '412'], ['Ledger entries', '2,486'], ['Total debits', '₹18.20 Cr'], ['Total credits', '₹18.20 Cr']].map(([k, v]) => (
          <div key={k} className="rounded-xl bg-receipt-40 px-3.5 py-3">
            <dt className="text-xs text-inkblack-60">{k}</dt>
            <dd className="mt-0.5 font-semibold tabular-nums text-inkblack">{v}</dd>
          </div>
        ))}
      </dl>
      <div className="mt-4 flex items-center justify-between rounded-xl bg-ok-50/70 px-3.5 py-3">
        <span className="inline-flex items-center gap-1.5 text-xs font-medium text-ok-700">
          <Scale className="h-3.5 w-3.5" />
          Debits and credits balanced to the paise
        </span>
        <TrendingUp className="h-4 w-4 text-ok-500" />
      </div>
    </div>
  );
}
